import type { ChatMessage } from "@loot-os/api-contract";
import { Reply } from "lucide-react";
import { memo } from "react";

import { mentionsViewer, splitMentions } from "../../lib/chat.js";
import { useProfile } from "../../lib/profile.js";
import { ChatActionsMenu } from "./ChatActionsMenu.js";
import { ChatAvatar } from "./ChatAvatar.js";
import { ReplyPreview } from "./ReplyPreview.js";

function timeLabel(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? ""
    : at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * One message (the Mystic7 Figma `Message`): the quote it replies to, then the player (avatar and
 * name, which open their profile) with the time and an actions pill (reply, more), then the text
 * with `@mentions` picked out. The row is highlighted when it mentions the viewer.
 */
export const ChatMessageRow = memo(function ChatMessageRow({
  message,
  viewerEndUserId,
  viewerUsername,
  highlighted,
  ignored,
  onReply,
  onReport,
  onDelete,
  onToggleIgnore,
  onJump,
}: {
  message: ChatMessage;
  viewerEndUserId: string | null;
  viewerUsername: string | null;
  highlighted: boolean;
  ignored: boolean;
  onReply: (message: ChatMessage) => void;
  onReport: (message: ChatMessage) => void;
  onDelete: (message: ChatMessage) => void;
  onToggleIgnore: (message: ChatMessage) => void;
  onJump: (messageId: string) => void;
}) {
  const { showProfile } = useProfile();
  const own = viewerEndUserId !== null && message.endUserId === viewerEndUserId;
  const mentioned = !own && !!message.text && mentionsViewer(message.text, viewerUsername);

  const signedIn = viewerEndUserId !== null;

  return (
    <div
      id={`chat-message-${message.messageId}`}
      className={`group flex flex-col gap-2 border-t border-border px-3 py-2 transition-colors ${
        highlighted ? "bg-primary/15" : mentioned ? "bg-primary/10" : ""
      }`}
    >
      {message.replyTo && <ReplyPreview replyTo={message.replyTo} onJump={onJump} />}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center">
          <button
            type="button"
            onClick={() => showProfile(message.endUserId)}
            className="flex min-w-0 items-center gap-2 rounded-full text-left"
          >
            <ChatAvatar endUserId={message.endUserId} displayName={message.displayName} />
            <span
              className={`truncate text-base leading-[22px] font-semibold tracking-[-0.4px] hover:underline ${
                own ? "text-primary" : "text-foreground"
              }`}
            >
              {message.displayName}
            </span>
          </button>
          <time
            dateTime={message.createdAt}
            className="shrink-0 px-2 text-xs leading-4 font-medium text-muted-foreground"
          >
            {timeLabel(message.createdAt)}
          </time>
        </div>
        {!message.deleted && (
          <div className="flex shrink-0 items-center gap-1 rounded-md border-[3px] border-card bg-muted px-1.5 py-0.5 text-muted-foreground">
            {signedIn && (
              <button
                type="button"
                onClick={() => onReply(message)}
                className="flex size-4 items-center justify-center hover:text-foreground"
                aria-label={`Reply to ${message.displayName}`}
              >
                <Reply className="size-4" />
              </button>
            )}
            <ChatActionsMenu
              message={message}
              own={own}
              signedIn={signedIn}
              ignored={ignored}
              onReport={() => onReport(message)}
              onDelete={() => onDelete(message)}
              onToggleIgnore={() => onToggleIgnore(message)}
            />
          </div>
        )}
      </div>
      {message.deleted ? (
        <p className="text-sm italic text-muted-foreground">Message deleted</p>
      ) : (
        <p className="text-sm leading-[22.75px] break-words whitespace-pre-wrap text-foreground">
          {splitMentions(message.text ?? "").map((part, i) =>
            part.mention ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional and static
              <span key={i} className="font-medium text-primary">
                {part.text}
              </span>
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional and static
              <span key={i}>{part.text}</span>
            ),
          )}
        </p>
      )}
    </div>
  );
});
